import { useCallback } from "react";
import { AlertOctagon } from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { CollapsibleSection } from "@/components/atoms/studio/CollapsibleSection";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { ValidationBadge } from "@/components/organisms/studio/ArtifactList";
import { CoveragePanel } from "./CoveragePanel";
import { EngineAdvisories } from "./EngineAdvisories";
import { EngineApplyPanel } from "./EngineApplyPanel";
import { EngineArtifactList } from "./EngineArtifactList";
import { EngineBlastRadiusPanel } from "./EngineBlastRadiusPanel";
import { EngineDecommissionSection } from "./EngineDecommissionSection";
import { ResiduePanel } from "./ResiduePanel";
import type { TaskReviewState } from "./reviewState";
import type { DisplayedArtifact } from "@/lib/migrations/apply";
import { asValidationOutcome, validationPresentation } from "@/lib/studio/validation";
import type {
  EngineArtifactRecord,
  EngineRefusal,
  EngineTaskReport,
} from "@/services/migrations/engine";

/**
 * One engine task: what it read, what it carried, what it dropped, what it produced, and
 * what applying it would do to the org.
 *
 * Order is deliberate. A REFUSAL outranks everything and is rendered first and alone — a
 * refused task has no coverage worth reading, and showing a coverage number beside a
 * refusal invites someone to quote the number.
 *
 * Otherwise the order tracks what a reader has to know before they can act on the next
 * thing: ADVISORIES first (behaviour that was carried and behaves differently — no
 * coverage number can show it, so nothing further down will mention it), then provenance
 * (which source, read whole or not), then coverage, then residue, then the artifacts
 * themselves, then the write path.
 *
 * ARTIFACTS DEFAULT OPEN while a decision is pending. The approval gate will not offer a
 * decision until the current artifact's body is on screen, and a section a reader has to
 * discover in order to unblock a button they cannot see is a puzzle, not a safeguard.
 */
export function EngineTaskCard({
  planId,
  task,
  family,
  orgId,
  successSuppressed,
  onReviewChange,
}: {
  planId: string;
  task: EngineTaskReport;
  /** The PLAN's family — a task entry carries none, and the decommission phase needs it. */
  family: string;
  orgId?: string | null;
  successSuppressed: boolean;
  /** Reports what this card is showing up to the plan-level approval gate. */
  onReviewChange?: (taskKey: string, state: Partial<TaskReviewState>) => void;
}) {
  const outcome = asValidationOutcome(task.validation?.outcome ?? task.validation_outcome);

  const handleArtifacts = useCallback(
    (state: { loaded: boolean; artifacts: EngineArtifactRecord[] }) =>
      onReviewChange?.(task.key, state),
    [onReviewChange, task.key]
  );
  const handleDisplayed = useCallback(
    (displayed: DisplayedArtifact | null) => onReviewChange?.(task.key, { displayed }),
    [onReviewChange, task.key]
  );

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/20 px-5 py-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate font-mono text-sm text-foreground">{task.key}</span>
          <RunStatusBadge status={task.status} />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {/* Same three-outcome chip the playbook artifact list uses — including the
              rule that `unvalidated` is a caution, not a failure. */}
          <ValidationBadge
            verdict={
              outcome
                ? {
                    outcome,
                    errors: task.validation?.errors ?? [],
                    reason: task.validation?.reason ?? null,
                  }
                : null
            }
            deployable
          />
        </div>
      </div>

      <div className="space-y-5 p-5">
        {task.refusal ? <RefusalPanel refusal={task.refusal} /> : null}

        {task.validation?.message ? (
          <p className="text-xs text-muted-foreground">
            {task.validation.message}
            {outcome ? <> {validationPresentation(outcome).detail}</> : null}
          </p>
        ) : null}

        {task.error ? <p className="font-mono text-xs text-destructive">{task.error}</p> : null}

        {task.refusal ? null : (
          <>
            <EngineAdvisories advisories={task.advisories} />

            <SourcePanel task={task} />
            <CoveragePanel planId={planId} task={task} successSuppressed={successSuppressed} />

            <CollapsibleSection
              title="Residue & manual steps"
              count={task.residue.length}
              defaultOpen={false}
            >
              <div className="p-5">
                <ResiduePanel residue={task.residue} />
              </div>
            </CollapsibleSection>

            {task.blast_radius ? (
              <CollapsibleSection
                title="What else automates this object"
                count={task.blast_radius.entries.length}
                defaultOpen={false}
              >
                <div className="p-5">
                  <EngineBlastRadiusPanel blastRadius={task.blast_radius} />
                </div>
              </CollapsibleSection>
            ) : null}

            <CollapsibleSection title="Artifacts" defaultOpen>
              <div className="p-5">
                <EngineArtifactList
                  planId={planId}
                  task={task}
                  onArtifactsChange={handleArtifacts}
                  onDisplayedChange={handleDisplayed}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Apply to org" defaultOpen={false}>
              <div className="p-5">
                <EngineApplyPanel planId={planId} task={task} orgId={orgId} />
              </div>
            </CollapsibleSection>

            {/* THE OTHER HALF OF THE MIGRATION. It sits after "Apply to org" because that
                is the order the acts happen in — and it DEFAULTS OPEN while the source is
                still live, because that is the state in which both automations may be
                firing and it is not a state a reader should have to go looking for. */}
            <CollapsibleSection
              title="Decommission the source"
              defaultOpen={task.decommission.double_fire_risk}
            >
              <div className="p-5">
                <EngineDecommissionSection
                  planId={planId}
                  task={task}
                  family={family}
                  orgId={orgId}
                />
              </div>
            </CollapsibleSection>

            <ProvenanceRow task={task} />
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A refusal is the engine declining to emit an artifact it cannot stand behind. It is
 * not a crash and not a partial result, and it is the only thing on the card when it
 * happens.
 */
function RefusalPanel({ refusal }: { refusal: EngineRefusal }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
      <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1 leading-relaxed">
        <p>
          <span className="font-mono font-semibold uppercase">{refusal.code}</span>
          {refusal.message ? <> — {refusal.message}</> : null}
        </p>
        {refusal.objects.length > 0 ? (
          <p className="break-words font-mono text-[11px] opacity-90">
            {refusal.objects.join(", ")}
          </p>
        ) : null}
        <p className="opacity-90">
          Nothing was produced for this task. This is a refusal, not a failure to try.
        </p>
      </div>
    </div>
  );
}

/**
 * Where the source came from and whether we saw all of it. `source_complete: false`
 * appears NEXT TO the coverage it weakens rather than in a log line, because a fraction
 * measured against a partial read is a different claim from the same fraction measured
 * against a whole one.
 */
function SourcePanel({ task }: { task: EngineTaskReport }) {
  const surfaces = Object.entries(task.source_counts);
  const sourceName = typeof task.source.full_name === "string" ? task.source.full_name : "";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          source
        </span>
        {sourceName ? (
          <span className="break-all font-mono text-xs text-foreground">{sourceName}</span>
        ) : null}
        <span className="font-mono text-xs text-muted-foreground">
          via {task.source_route || "unknown"}
        </span>
        <Badge variant={task.source_complete ? "secondary" : "warm"}>
          {task.source_complete ? "read in full" : "partial read"}
        </Badge>
        {task.menus_unavailable.length > 0 ? (
          <Badge variant="warm">{task.menus_unavailable.length} arm(s) unavailable</Badge>
        ) : null}
      </div>
      {surfaces.length > 0 ? (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-muted-foreground">
          {surfaces.map(([surface, [org, body]]) => (
            <li key={surface} className={org === body ? "" : "text-[hsl(var(--accent-warm))]"}>
              {surface}: {body}/{org}
            </li>
          ))}
        </ul>
      ) : null}
      {task.scope_objects.length > 0 ? (
        <p className="font-mono text-[11px] text-muted-foreground">
          scope: {task.scope_objects.join(", ")}
        </p>
      ) : null}
    </div>
  );
}

/** The shas that make a verdict reproducible. Small, monospaced, never the headline. */
function ProvenanceRow({ task }: { task: EngineTaskReport }) {
  const rows: Array<[string, string]> = [
    ["contract", task.contract_sha],
    ["compile key", task.compile_key],
    ["artifact", task.artifact_sha],
    ["menus", task.menus_sha],
    ["menus scope", task.menus_scope_sha],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-3 font-mono text-[10px] text-muted-foreground/70">
      {rows.map(([label, value]) => (
        <span key={label}>
          {label} {value.slice(0, 12)}
        </span>
      ))}
    </div>
  );
}
